import * as React from "react";
import {
    Box,
    IconButton,
    MenuItem,
    TextField,
    Button,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import { Add, Close, Delete } from "@mui/icons-material";
import {
    GridFilterItem,
    GridFilterModel,
    useGridApiContext,
    gridFilterModelSelector,
    useGridSelector,
} from "@mui/x-data-grid-pro";
import {
    NoInput,
    TextInput,
    NumberInput,
    DateInput,
    MultipleInput,
    BooleanInput,
    DateTimeInput,
} from "./CustomFilterInputs";
import { SingleSelectInput } from "./CustomFilterInputs";

type OperatorOption = { value: string; label: string };

type LogicOperatorValue = "and" | "or";

const isNoValueOperator = (op: string) =>
    op === "isEmpty" ||
    op === "isNotEmpty" ||
    op === "isNull" ||
    op === "isNotNull";

const getLogicOperator = (m: GridFilterModel): LogicOperatorValue => {
    const v = (m as any).logicOperator ?? (m as any).linkOperator;
    return v === "or" ? "or" : "and";
};

export default function CustomFilterPanel() {
    const apiRef = useGridApiContext();
    const rawModel = useGridSelector(apiRef, gridFilterModelSelector);
    const model: GridFilterModel = {
        ...rawModel,
        items: rawModel.items.map((it: any) =>
            "columnField" in it
                ? {
                    id: it.id,
                    field: it.columnField,
                    operator: it.operatorValue,
                    value: it.value,
                }
                : it,
        ),
    };

    const setModel = React.useCallback(
        (newModel: GridFilterModel) => apiRef.current.setFilterModel(newModel),
        [apiRef],
    );

    const columns = apiRef.current.getAllColumns().filter((c) => c.filterable);

    const modelRef = React.useRef(model);
    React.useEffect(() => {
        modelRef.current = model;
    });

    React.useEffect(() => {
        if (!columns.length || model.items?.length) return;

        const firstCol = columns[0];
        const ops = getColumnOperators(firstCol);
        const firstOp = ops[0]?.value || "";
        const id = Math.random().toString(36).slice(2);
        setModel({
            ...model,
            items: [{ id, field: firstCol.field, operator: firstOp, value: "" }],
        });
    }, [columns, model.items?.length]);

    React.useEffect(() => {
        const api = apiRef.current;
        return () => {
            const current = modelRef.current;
            const isIncomplete = (it: GridFilterItem) =>
                !isNoValueOperator(it.operator) &&
                (it.value === "" || it.value === undefined || it.value === null ||
                    (Array.isArray(it.value) && it.value.length === 0));
            const cleanItems = current.items.filter((it) => !isIncomplete(it));
            if (cleanItems.length !== current.items.length) {
                api.setFilterModel({ ...current, items: cleanItems });
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getLocaleTextString = (key: string) =>
        String((apiRef.current as any).getLocaleText(key as any));

    const toLabel = (key: string) => {
        return key
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const getOperatorLabel = (value: string, rawLabel?: string) => {
        const localeKey =
            "filterOperator" +
            value.charAt(0).toUpperCase() +
            value.slice(1);

        const localized = getLocaleTextString(localeKey);

        if (localized && localized !== localeKey) {
            return localized;
        }

        if (rawLabel && rawLabel.trim().length > 0) {
            return rawLabel;
        }

        return toLabel(value);
    };

    const getColumnOperators = (col: any): OperatorOption[] => {
        if (col?.filter) {
            const arr = Array.isArray(col?.filter) ? col.filter : [];

            return arr.map((op: any) => {
                if (typeof op === "string") {
                    return {
                        value: op,
                        label: getOperatorLabel(op),
                    };
                }
                const value = String(op?.value ?? "");
                const rawLabel = op?.label ? String(op.label) : undefined;
                return {
                    value,
                    label: getOperatorLabel(value, rawLabel),
                };
            });
        } else {
            const fo = Array.isArray(col?.filterOperators)
                ? col.filterOperators
                : [];
            return fo.map((op: any) => {
                const value = String(op?.value ?? "");
                const rawLabel = op?.label ? String(op.label) : undefined;
                return {
                    value,
                    label: getOperatorLabel(value, rawLabel),
                };
            });
        }
    };

    const updateItem = (
        id: GridFilterItem["id"],
        patch: Partial<GridFilterItem>,
    ) => {
        setModel({
            ...model,
            items: model.items.map((it) =>
                it.id === id ? { ...it, ...patch } : it,
            ),
        });
    };

    const addFilter = () => {
        if (!columns.length) return;
        const col = columns[0];
        const ops = getColumnOperators(col);
        const firstOp = ops[0]?.value || "";
        const id = Math.random().toString(36).slice(2);
        setModel({
            ...model,
            items: [
                ...model.items,
                { id, field: col.field, operator: firstOp, value: "" },
            ],
        });
    };

    const removeItem = (id: GridFilterItem["id"]) => {
        setModel({ ...model, items: model.items.filter((i) => i.id !== id) });
    };

    const clearAll = () => setModel({ ...model, items: [] });

    const renderValueInput = (item: GridFilterItem) => {
        const column = apiRef.current.getColumn(item.field);

        if (isNoValueOperator(item.operator)) return <NoInput />;
        
        if (item.operator === "contains") {
            return (
                <TextInput
                    item={item}
                    onChange={(v) => updateItem(item.id, { value: v })}
                />
            );
        }
        const raw = (column as any)?.valueOptions || [];

        const valueOptions = raw.map((opt: any) =>
            typeof opt === "string"
                ? { value: opt, label: opt }
                : {
                    value: String(opt.value),
                    label: String(opt.label ?? opt.value),
                }
        );
        const isMulti =
            item.field === "callOffStatus" && item.operator === "isAnyOf";
        if (isMulti) {
            return (
                <MultipleInput
                    item={item}
                    valueOptions={valueOptions}
                    onChange={(v) => updateItem(item.id, { value: v })}
                />
            );
        }

        // If the column provides valueOptions, render a single-select input
        if (valueOptions.length > 0) {
            return (
                <SingleSelectInput
                    item={item}
                    valueOptions={valueOptions}
                    onChange={(v) => updateItem(item.id, { value: v })}
                />
            );
        }
        const colType = String((column as any)?.type);

        switch (colType) {
            case "string":
                return (
                    <TextInput
                        item={item}
                        onChange={(v) => updateItem(item.id, { value: v })}
                    />
                );
            case "number":
                return (
                    <NumberInput
                        item={item}
                        onChange={(v) => updateItem(item.id, { value: v })}
                    />
                );
            case "date":
                return (
                    <DateInput
                        item={item}
                        onChange={(v) => updateItem(item.id, { value: v })}
                    />
                );
            case "dateTime":
            case "timeDuration":
                return (
                    <DateTimeInput
                        item={item}
                        onChange={(v) => updateItem(item.id, { value: v })}
                    />
                );
            case "boolean":
                return (
                    <BooleanInput
                        item={item}
                        valueOptions={valueOptions}
                        onChange={(v) => updateItem(item.id, { value: v })}
                    />
                );
            default:
                return (
                    <TextInput
                        item={item}
                        onChange={(v) => updateItem(item.id, { value: v })}
                    />
                );
        }
    };

    const setLogicOperator = (op: LogicOperatorValue) => {
        setModel({ ...(model as any), logicOperator: op } as any);
    };

    const AND_WIDTH = 88;

    return (
        <Box
            sx={{
                p: 1,
                width: 900,
                display: "flex",
                flexDirection: "column",
                gap: 1,
            }}
        >
            {model.items.map((item, idx) => {
                const col = apiRef.current.getColumn(item.field);
                const ops = getColumnOperators(col);
                const hasMultiple = model.items.length > 1;
                return (
                    <Box
                        key={item.id}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "auto 1fr 1fr 1fr auto",
                            columnGap: 2,
                            pt: 2,
                            alignItems: "center",
                        }}
                    >
                        {idx === 0 ? (
                            <Box
                                sx={{ minWidth: hasMultiple ? AND_WIDTH : 0 }}
                            />
                        ) : (
                            <ToggleButtonGroup
                                exclusive
                                value={getLogicOperator(model)}
                                onChange={(e, val) =>
                                    val && setLogicOperator(val)
                                }
                                sx={{
                                    width: AND_WIDTH,
                                    height: 40,
                                    borderRadius: 1,
                                    "& .MuiToggleButton-root": {
                                        px: 1.25,
                                        py: 0.5,
                                        textTransform: "none",
                                        fontSize: 12,
                                        border: "1px solid",
                                        borderColor: "divider",
                                    },
                                    "& .MuiToggleButton-root.Mui-selected": {
                                        backgroundColor: "action.selected",
                                    },
                                }}
                                aria-label={getLocaleTextString("filterPanelLogicOperator")}
                            >
                                <ToggleButton value="and">
                                    {getLocaleTextString("filterPanelOperatorAnd")}
                                </ToggleButton>
                                <ToggleButton value="or">
                                    {getLocaleTextString("filterPanelOperatorOr")}
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        <TextField
                            size="small"
                            select
                            fullWidth
                            label={getLocaleTextString("filterPanelColumns")}
                            variant="outlined"
                            value={item.field}
                            onChange={(e) => {
                                const newField = e.target.value;
                                const newCol =
                                    apiRef.current.getColumn(newField);
                                const newOps = getColumnOperators(newCol);
                                const firstOp = newOps[0]?.value || "";
                                updateItem(item.id, {
                                    field: newField,
                                    operator: firstOp,
                                    value: "",
                                });
                            }}
                        >
                            {columns.map((c) => (
                                <MenuItem key={c.field} value={c.field}>
                                    {c.headerName || c.field}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            size="small"
                            select
                            fullWidth
                            label={getLocaleTextString("filterPanelOperator")}
                            variant="outlined"
                            value={item.operator}
                            onChange={(e) => {
                                const nextOp = e.target.value;
                                updateItem(item.id, {
                                    operator: nextOp,
                                    value: isNoValueOperator(nextOp)
                                        ? undefined
                                        : item.value,
                                });
                            }}
                        >
                            {ops.map((op) => (
                                <MenuItem key={op.value} value={op.value}>
                                    {op.label}
                                </MenuItem>
                            ))}
                        </TextField>

                        {renderValueInput(item)}

                        <Box
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                            <IconButton
                                size="small"
                                onClick={() => removeItem(item.id)}
                                aria-label="remove-filter"
                            >
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>
                );
            })}

            <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
            >
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addFilter}
                >
                    {getLocaleTextString("filterPanelAddFilter")}
                </Button>
                <Button
                    size="small"
                    startIcon={<Delete />}
                    variant="outlined"
                    onClick={clearAll}
                >
                    {getLocaleTextString("filterPanelRemoveAll")}
                </Button>
            </Box>
        </Box>
    );
}
